import {
    Mic2,
    GalleryVertical,
    Store,
    Handshake,
    Coffee,
    Briefcase,
    Crown,
    Plane,
    GraduationCap,
    Factory,
    Building2,
    Hotel,
} from "lucide-react";
import { Reveal, Chapter } from "@/components/lunar/Reveal";
import { LunarMark } from "@/components/lunar/Logo";
import { StoreButtons } from "@/components/lunar/StoreButtons";
export { NetworkingWhoMightYouFind } from "@/components/lunar/WhoMightYouFind";

const STEPS = [
    {
        number: "01",
        title: "Tell Lunar",
        copy: "Add what you do and who it would be useful for you to meet.",
    },
    {
        number: "02",
        title: "Find people",
        copy: "See professionals around you and explore who’s there. Lunar can also identify particularly relevant people in the background.",
    },
    {
        number: "03",
        title: "Wave",
        copy: "See someone worth meeting? Send a Wave. If they Wave back, you have ten messages to find each other.",
    },
];

export const NetworkingHow = () => (
    <section id="networking-how" className="mx-auto max-w-6xl scroll-mt-8 px-6 py-24 sm:py-28" data-testid="networking-how-section">
        <Reveal>
            <Chapter index="01" label="How it works" />
            <h2 className="font-display mt-5 max-w-2xl text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                Find the right people in the room.
            </h2>
            <p
                data-testid="networking-how-subheading"
                className="mt-5 max-w-xl text-base leading-relaxed text-[#5C5F58] sm:text-lg"
            >
                Browse the people around you, or let Lunar help identify someone professionally
                relevant.
            </p>
        </Reveal>
        <div className="mt-16 grid gap-12 md:grid-cols-3 md:gap-8">
            {STEPS.map((step, i) => (
                <Reveal key={step.number} delay={i * 0.12} data-testid={`networking-step-${i + 1}`}>
                    <div className="border-t border-[#C2C0B8] pt-7">
                        <span className="font-display text-5xl font-medium leading-none text-[#899B6A]">
                            {step.number}
                        </span>
                        <h3 className="font-display mt-5 text-2xl font-semibold text-[#1D201A]">
                            {step.title}
                        </h3>
                        <p className="mt-2 max-w-xs text-base leading-relaxed text-[#5C5F58]">
                            {step.copy}
                        </p>
                    </div>
                </Reveal>
            ))}
        </div>
        <Reveal delay={0.15}>
            <p className="font-display mt-16 text-center text-2xl font-medium italic text-[#4A5D23] sm:text-3xl">
                Then Lunar has done her job.
            </p>
        </Reveal>
    </section>
);

export const NetworkingRecommendation = () => (
    <section className="bg-[#1D201A]" data-testid="networking-recommendation-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-24 sm:py-28 lg:grid-cols-2 lg:gap-20">
            <Reveal>
                <Chapter index="03" label="The moment" dark />
                <h2 className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#F9F8F4] sm:text-5xl">
                    Someone worth meeting is here.
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-[#D5D8CB] sm:text-lg">
                    Not another directory of professionals. A reason to meet someone who’s actually
                    there.
                </p>
            </Reveal>
            <Reveal delay={0.12}>
                <div data-testid="networking-recommendation-card" className="rounded-3xl border border-[#2A2C27] bg-[#20221E] p-7 sm:p-8">
                    <div className="flex items-center gap-2">
                        <LunarMark size={18} className="text-[#899B6A]" />
                        <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-[#899B6A]">
                            Someone worth meeting is here 🌿
                        </p>
                    </div>
                    <p className="font-display mt-5 text-3xl font-semibold text-[#F9F8F4]">Priya</p>
                    <p className="text-sm text-[#A8B888]">Investment Director</p>
                    <p className="mt-4 text-sm leading-relaxed text-[#D5D8CB]">
                        Priya is looking for hospitality investment opportunities that overlap with
                        what you do.
                    </p>
                    <span className="mt-6 inline-flex rounded-full bg-[#899B6A] px-6 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-[#171816]">
                        Send Wave
                    </span>
                </div>
            </Reveal>
        </div>
    </section>
);

const REMEMBER_CHIPS = [
    "Who",
    "Where",
    "When",
    "Why you connected",
    "What they were looking for",
    "Private notes",
];

export const NetworkingRemembers = () => (
    <section className="mx-auto max-w-6xl px-6 py-24 sm:py-28" data-testid="networking-remembers-section">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
            <Reveal>
                <Chapter index="04" label="Lunar remembers" />
                <h2 className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                    Lunar remembers who you met. And why.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-[#3E413B] sm:text-lg">
                    Months later, instead of wondering “Who was that person I met at that
                    networking event who was looking for hotels?”, Lunar can help you remember who
                    you met, where you met them and what they were looking for at the time.
                </p>
                <div className="mt-7 flex flex-wrap gap-2">
                    {REMEMBER_CHIPS.map((chip) => (
                        <span
                            key={chip}
                            className="rounded-full border border-[#C2C0B8] px-4 py-2 text-xs font-medium text-[#3E413B]"
                        >
                            {chip}
                        </span>
                    ))}
                </div>
            </Reveal>
            <Reveal delay={0.12}>
                <div className="rounded-3xl border border-[#E5E3DC] bg-[#F0EFEB] p-7 sm:p-9">
                    <div className="flex flex-wrap items-center gap-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#899B6A]">
                            AI search across your real-world professional network
                        </p>
                        <span
                            data-testid="networking-ai-search-chip"
                            className="rounded-full bg-[#2C3618] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#F9F8F4]"
                        >
                            AI Search coming soon.
                        </span>
                    </div>
                    <p className="font-display mt-4 text-xl italic leading-snug text-[#3E413B] sm:text-2xl">
                        “Who did I meet last year who was looking to invest in hotels?”
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);

const NETWORKING_PLACES = [
    { icon: Mic2, label: "Conference" },
    { icon: GalleryVertical, label: "Exhibition" },
    { icon: Store, label: "Trade show" },
    { icon: Handshake, label: "Networking event" },
    { icon: Coffee, label: "Business breakfast" },
    { icon: Briefcase, label: "Coworking space" },
    { icon: Crown, label: "Members’ club" },
    { icon: Plane, label: "Airport" },
    { icon: Hotel, label: "Hotel" },
    { icon: GraduationCap, label: "University" },
    { icon: Factory, label: "Industry event" },
    { icon: Building2, label: "Corporate event" },
];

export const NetworkingPlaces = () => (
    <section className="mx-auto max-w-6xl px-6 py-24 sm:py-28" data-testid="networking-places-section">
        <Reveal>
            <Chapter index="05" label="Where it works" />
            <h2 className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                Wherever work happens.
            </h2>
        </Reveal>
        <Reveal delay={0.08}>
            <figure className="mt-12" data-testid="networking-places-illustration">
                <img
                    src="/assets/networking/network-wherever-work-illustrative.webp"
                    alt="Illustrative Lunar branding at a conference and networking event"
                    data-testid="networking-places-illustration-image"
                    loading="lazy"
                    className="w-full rounded-3xl border border-[#E5E3DC] object-cover"
                />
                <figcaption
                    data-testid="networking-places-illustration-caption"
                    className="mt-3 text-xs tracking-wide text-[#5C5F58] opacity-75"
                >
                    Illustrative only.
                </figcaption>
            </figure>
        </Reveal>
        <Reveal delay={0.1}>
            <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="networking-places-grid">
                {NETWORKING_PLACES.map(({ icon: Icon, label }) => (
                    <div
                        key={label}
                        data-testid={`networking-place-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="group flex flex-col items-start gap-5 rounded-2xl border border-[#E5E3DC] bg-[#F9F8F4] p-5 transition-colors duration-300 hover:border-[#899B6A]"
                    >
                        <Icon
                            className="h-6 w-6 text-[#4A5D23] transition-colors duration-300 group-hover:text-[#2C3618]"
                            strokeWidth={1.5}
                        />
                        <span className="text-sm font-medium text-[#3E413B]">{label}</span>
                    </div>
                ))}
            </div>
        </Reveal>
    </section>
);

export const NetworkingFinalCta = () => (
    <section className="mx-auto max-w-6xl px-6 py-24 sm:py-32" data-testid="networking-final-cta">
        <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-[2.5rem] font-medium leading-[1.08] tracking-tight text-[#1D201A] sm:text-6xl">
                Someone worth meeting could be{" "}
                <em className="italic text-[#4A5D23]">closer than you think</em>.
            </h2>
            <div className="mt-10">
                <StoreButtons
                    centered
                    appStoreTestId="networking-final-app-store"
                    googlePlayTestId="networking-final-google-play"
                />
            </div>
            <p className="mt-6 text-xs font-medium uppercase tracking-[0.22em] text-[#5C5F58]">
                Open Lunar Networking
            </p>
            <p className="mt-12 text-sm text-[#5C5F58]">
                Running something bigger?{" "}
                <a href="/events" className="font-medium text-[#4A5D23] underline decoration-[#C2C0B8] underline-offset-4 hover:text-[#2C3618]">
                    Lunar for Events
                </a>
                {" · "}
                <a href="/venue-kit" className="font-medium text-[#4A5D23] underline decoration-[#C2C0B8] underline-offset-4 hover:text-[#2C3618]">
                    Lunar for Venues
                </a>
            </p>
            <p className="font-display mt-14 text-2xl font-medium italic text-[#4A5D23] sm:text-3xl">
                Lunar — life should be done in person.
            </p>
        </Reveal>
    </section>
);
